/*!
 * Copyright © 2023. Verizon Connect Ireland Limited. All rights reserved.
 */

import { StepperSelectionEvent, StepState } from '@angular/cdk/stepper';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { EBoxType, EPingState, IUserSettings, PingAndResetSupportedDevices } from '../../../core/models';
import { EHealthCheckRequestState } from '../../models';

@Component({
  selector: 'fm-device-health-check-ping-and-reset',
  templateUrl: './device-health-check-ping-and-reset.component.html',
  styleUrls: ['./device-health-check-ping-and-reset.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceHealthCheckPingAndResetComponent implements OnChanges {
  @Input()
  isAsset: boolean;
  @Input()
  boxType: EBoxType;
  @Input()
  userSettings: IUserSettings;
  @Input()
  pingState: EHealthCheckRequestState;
  @Input()
  resetState: EHealthCheckRequestState;
  @Input()
  isPingSupported: boolean;
  @Input()
  isNonPoweredAsset: boolean;

  @Output()
  resetClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  showContactUs: EventEmitter<boolean> = new EventEmitter<boolean>();

  vehicleTroubleshootingUrl: string;
  assetTroubleshootingUrl: string;
  restartStepState: 'NotSent' | 'PingPending' | 'PingFailed' | 'ResetPending' | 'ResetSuccess' | 'ResetFailed' = 'NotSent';
  restartSuccessConfirmed: boolean | null = null;

  EHealthCheckRequestState = EHealthCheckRequestState;
  EPingState = EPingState;

  @ViewChild('stepper', { read: MatStepper })
  private readonly _stepper: MatStepper;

  constructor(private readonly _changeDetector: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.isPingSupported = PingAndResetSupportedDevices.includes(this.boxType);
    const language = this.userSettings.Language.toLocaleLowerCase();
    this.assetTroubleshootingUrl = `https://install.verizonconnect.com/hc/${language}/articles/5518969124755-FAQ-What-to-do-when-an-asset-tracker-is-not-reporting-`;
    this.vehicleTroubleshootingUrl = `https://install.verizonconnect.com/hc/${language}/articles/360060393373-Why-is-my-vehicle-not-reporting-`;

    if (this.isPingSupported && changes.statusItem?.currentValue?.id !== changes.statusItem?.previousValue?.id) {
      this.resetStepperToStart();
    }

    this._handlePingResetStateChange();
  }

  resetStepperToStart(stepperEvent?: StepperSelectionEvent): void {
    if (!stepperEvent) {
      this._stepper.previous();
    }
    if (!stepperEvent || stepperEvent.selectedIndex === 0) {
      this.restartStepState = 'NotSent';
      this.restartSuccessConfirmed = null;
      this._setRestartStepState('edit');
      this._changeDetector.detectChanges();
    }
  }

  onRestartClicked(): void {
    this.resetClicked.emit();
  }

  onRestartProblemFixedYes(): void {
    this.restartSuccessConfirmed = true;
  }

  onRestartProblemFixedNo(): void {
    this.restartSuccessConfirmed = false;
    this.showContactUs.emit(true);
  }

  private _handlePingResetStateChange() {
    this.restartSuccessConfirmed = null;
    if (this.pingState === EHealthCheckRequestState.NotSent) {
      this.restartStepState = 'NotSent';
      this.showContactUs.emit(false);
    } else if (this.pingState === EHealthCheckRequestState.Pending) {
      this.restartStepState = 'PingPending';
      this._setRestartStepState('edit');
      this.showContactUs.emit(false);
    } else if (this.pingState === EHealthCheckRequestState.Failure) {
      this.restartStepState = 'PingFailed';
      this._setRestartStepState('error');
      this.showContactUs.emit(true);
    } else if (this.resetState === EHealthCheckRequestState.Pending) {
      this.restartStepState = 'ResetPending';
      this._setRestartStepState('edit');
      this.showContactUs.emit(false);
    } else if (this.resetState === EHealthCheckRequestState.Success) {
      this.restartStepState = 'ResetSuccess';
      this._setRestartStepState('done');
      this.showContactUs.emit(false);
    } else if (this.resetState === EHealthCheckRequestState.Failure) {
      this.restartStepState = 'ResetFailed';
      this._setRestartStepState('error');
      this.showContactUs.emit(true);
    }
  }

  private _setRestartStepState(state: StepState): void {
    this._stepper.steps.get(1).state = state;
    this._stepper.steps.get(1).completed = state === 'error' || state === 'done';
  }
}
